/**
 * useInactivityLogout
 *
 * - Logs the user out after INACTIVITY_MS of no activity (default: 60 minutes)
 * - Silently refreshes the Cognito access/id tokens when the user IS active
 *   and the token is within REFRESH_BEFORE_EXPIRY_S seconds of expiring.
 *   This prevents the hard 30-minute Cognito expiry from kicking active users out.
 */

import { useEffect, useRef, useCallback } from 'react';
import { COGNITO_CLIENT_ID, COGNITO_DOMAIN } from '../config';
import { clearAuthStorage } from '../utils/authStorage';

const INACTIVITY_MS = 60 * 60 * 1000;   // 60 minutes idle → logout
// Start refreshing when < 15 minutes remain.  The background interval fires
// every 4 minutes, so worst-case the token is renewed with 11 minutes to spare —
// well before the Lambda authorizer would reject it.
const REFRESH_BEFORE_EXPIRY_S = 15 * 60;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

function decodeTokenExp(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

export async function silentTokenRefresh(): Promise<void> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken || !COGNITO_DOMAIN || !COGNITO_CLIENT_ID) return;

  try {
    const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: COGNITO_CLIENT_ID,
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      console.warn('Silent token refresh failed — response not ok');
      return;
    }

    const data = await response.json();
    if (data.id_token) localStorage.setItem('idToken', data.id_token);
    if (data.access_token) localStorage.setItem('accessToken', data.access_token);
    // Note: Cognito does NOT return a new refresh_token for refresh_token grants
    console.info('Token silently refreshed');
  } catch (err) {
    console.warn('Silent token refresh error:', err);
  }
}

function logout(): void {
  const redirectPath = window.location.pathname + window.location.search || '/dashboard';
  clearAuthStorage();
  try {
    sessionStorage.setItem('redirectAfterLogin', redirectPath);
  } catch {
    // ignore
  }
  window.location.href = '/login';
}

export function useInactivityLogout(): void {
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRefreshing = useRef(false);

  const resetTimer = useCallback(() => {
    // Don't do anything if the user isn't actually logged in
    // (e.g. activity events firing on the login page while typing credentials)
    const idToken = localStorage.getItem('idToken');
    if (!idToken) return;

    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(async () => {
      // Double-check token still exists before logging out
      const idToken = localStorage.getItem('idToken');
      if (!idToken) return;

      // Make one last refresh attempt — the user may have just come back
      // to an idle tab where the token is still technically valid but stale.
      // Only proceed with logout if the token is genuinely expired afterward.
      if (!isRefreshing.current) {
        isRefreshing.current = true;
        await silentTokenRefresh().catch(() => {}).finally(() => { isRefreshing.current = false; });
      }

      const stillValid = (() => {
        const t = localStorage.getItem('idToken');
        if (!t) return false;
        const exp = decodeTokenExp(t);
        return exp !== null && exp - Math.floor(Date.now() / 1000) > 0;
      })();

      if (!stillValid) {
        console.info('Logging out due to inactivity — session could not be refreshed.');
        logout();
      } else {
        // Token was successfully refreshed; reset the inactivity timer once more
        // so the user doesn't get booted immediately after returning
        console.info('Inactivity timer fired but token refreshed — resetting timer.');
        resetTimer();
      }
    }, INACTIVITY_MS);

    // Check if token needs refreshing while user is active
    if (isRefreshing.current) return;
    const exp = decodeTokenExp(idToken);
    if (exp !== null) {
      const secondsLeft = exp - Math.floor(Date.now() / 1000);
      if (secondsLeft < REFRESH_BEFORE_EXPIRY_S) {
        isRefreshing.current = true;
        silentTokenRefresh().finally(() => {
          isRefreshing.current = false;
        });
      }
    }
  }, []);

  useEffect(() => {
    // Always attach listeners so they're ready as soon as the user logs in.
    // resetTimer and the refresh interval both guard on idToken internally,
    // so they're safe to run when no session exists.

    // Start the timer immediately if already logged in
    if (localStorage.getItem('idToken')) resetTimer();

    // Attach activity listeners
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, resetTimer, { passive: true }));

    // Background proactive refresh every 4 minutes — covers users who are idle
    // but still within the inactivity window (reading, watching, etc.) and generate
    // no mouse/keyboard events that would trigger the activity-path refresh.
    // 4-minute interval + 15-minute threshold = token renewed ≥11 min before expiry.
    const refreshInterval = setInterval(() => {
      const token = localStorage.getItem('idToken');
      if (!token || isRefreshing.current) return;
      const exp = decodeTokenExp(token);
      if (exp !== null) {
        const secondsLeft = exp - Math.floor(Date.now() / 1000);
        if (secondsLeft < REFRESH_BEFORE_EXPIRY_S) {
          isRefreshing.current = true;
          void silentTokenRefresh().finally(() => {
            isRefreshing.current = false;
          });
        }
      }
    }, 4 * 60 * 1000);

    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      clearInterval(refreshInterval);
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [resetTimer]);
}
